import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import PlaceCard from "../components/PlaceCard";
import MapView from "../components/MapView";
import { RESTRICTIONS, BRAZIL_STATES } from "../constants/restrictions";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, MapIcon, Grid2x2 } from "lucide-react";
import BannerSlot from "../components/BannerSlot";

function renderResults(view, places) {
  if (view === "map") return <MapView places={places} height="600px" />;
  if (places.length === 0) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-brand-border p-16 text-center" data-testid="empty-state">
        <p className="font-heading font-bold text-2xl text-brand-text">Nenhum lugar encontrado</p>
        <p className="mt-2 text-brand-textMuted">Que tal indicar um lugar seguro que você conhece?</p>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="places-grid">
      {places.map((p) => <PlaceCard key={p.place_id} place={p} />)}
    </div>
  );
}

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("grid");

  const restriction = params.get("restriction") || "";
  const state = params.get("state") || "";
  const city = params.get("city") || "";
  const q = params.get("q") || "";

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ status: "approved" });
    if (restriction) qs.set("restriction", restriction);
    if (state) qs.set("state", state);
    if (city) qs.set("city", city);
    if (q) qs.set("q", q);
    api.get(`/places?${qs.toString()}`)
      .then((r) => setPlaces(r.data))
      .finally(() => setLoading(false));
  }, [restriction, state, city, q]);

  return (
    <div data-testid="explore-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <BannerSlot placement="explore_top" />
      <div className="mb-10">        <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-secondary">Explorar lugares</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl text-brand-text mt-2">Encontre seu próximo lugar seguro</h1>
        <p className="mt-3 text-brand-textMuted max-w-2xl">Filtre por cidade, estado ou restrição alimentar — todos os lugares foram aprovados por moderadores.</p>
      </div>

      {/* Filters */}
      <div className="rounded-3xl bg-white border border-brand-border shadow-sm p-5 md:p-6 mb-8 grid md:grid-cols-12 gap-3" data-testid="filters-bar">
        <div className="md:col-span-4 relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-brand-textMuted" />
          <Input
            data-testid="search-input"
            placeholder="Buscar lugar..."
            className="pl-10 rounded-full h-12 bg-brand-bg border-brand-border"
            defaultValue={q}
            onKeyDown={(e) => { if (e.key === "Enter") updateParam("q", e.currentTarget.value); }}
          />
        </div>
        <Input
          data-testid="city-filter"
          placeholder="Cidade"
          className="md:col-span-3 rounded-full h-12 bg-brand-bg border-brand-border"
          defaultValue={city}
          onKeyDown={(e) => { if (e.key === "Enter") updateParam("city", e.currentTarget.value); }}
        />
        <select
          data-testid="state-filter"
          value={state}
          onChange={(e) => updateParam("state", e.target.value)}
          className="md:col-span-2 rounded-full h-12 bg-brand-bg border border-brand-border px-4 font-bold text-sm"
        >
          <option value="">UF</option>
          {BRAZIL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          data-testid="restriction-filter"
          value={restriction}
          onChange={(e) => updateParam("restriction", e.target.value)}
          className="md:col-span-3 rounded-full h-12 bg-brand-bg border border-brand-border px-4 font-bold text-sm"
        >
          <option value="">Todas restrições</option>
          {RESTRICTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      {/* View toggle */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-bold text-brand-textMuted">{loading ? "Buscando..." : `${places.length} lugar(es) encontrado(s)`}</p>
        <div className="inline-flex bg-white border border-brand-border rounded-full p-1">
          <button
            data-testid="view-grid"
            onClick={() => setView("grid")}
            className={`px-4 py-2 rounded-full font-bold text-sm inline-flex items-center gap-1.5 ${view === "grid" ? "bg-brand-primary text-white" : "text-brand-text"}`}
          ><Grid2x2 className="w-4 h-4" /> Lista</button>
          <button
            data-testid="view-map"
            onClick={() => setView("map")}
            className={`px-4 py-2 rounded-full font-bold text-sm inline-flex items-center gap-1.5 ${view === "map" ? "bg-brand-primary text-white" : "text-brand-text"}`}
          ><MapIcon className="w-4 h-4" /> Mapa</button>
        </div>
      </div>

      {renderResults(view, places)}
    </div>
  );
}
